import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Mail, Loader2, CheckCircle, Sparkles } from 'lucide-react';
import { useAccess } from '../App';
import { submitEmailSignup } from '../utils/api';

function EmailCapture({ onClose }) {
  const { sessionId, accessCode } = useAccess();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [userType, setUserType] = useState('investor');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError(null);

    try {
      await submitEmailSignup({
        email: email.trim(),
        name: name.trim(),
        userType,
        source: 'pitch',
        sessionId,
        accessCode
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to sign up. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-surface-card border border-surface-border rounded-2xl w-full max-w-md overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-surface-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-accent-mint/10">
              <Mail className="w-5 h-5 text-accent-mint" />
            </div>
            <div>
              <h2 className="font-semibold text-white">Stay Updated</h2>
              <p className="text-xs text-zinc-500">Get KoreFolio news & updates</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/5 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {success ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-8"
            >
              <div className="w-16 h-16 rounded-full bg-accent-mint/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-accent-mint" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">You're on the list!</h3>
              <p className="text-zinc-400 text-sm mb-6">
                We'll keep you updated on KoreFolio's progress.
              </p>
              <button onClick={onClose} className="btn-primary">
                Continue Viewing
              </button>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="input-field"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Name (optional)
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  I am a...
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'investor', label: 'Investor' },
                    { value: 'talent', label: 'Beauty Professional' },
                    { value: 'business', label: 'Salon/Spa Owner' },
                    { value: 'other', label: 'Other' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setUserType(option.value)}
                      className={`px-3 py-2 rounded-lg text-sm transition-all ${
                        userType === option.value
                          ? 'bg-kore-500 text-white'
                          : 'bg-surface-elevated text-zinc-400 hover:text-white'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {error && (
                <p className="text-red-400 text-sm">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading || !email.trim()}
                className="w-full btn-primary flex items-center justify-center gap-2"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Subscribe
                  </>
                )}
              </button>

              <p className="text-xs text-zinc-500 text-center">
                We respect your privacy. Unsubscribe anytime.
              </p>
            </form>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default EmailCapture;
